import React from 'react';
import clsx from 'clsx';

const IMD_MAX_RANK = 32844;

const CRIME_TIER = {
  'Low': 4,
  'Below Average': 3,
  'Average': 2,
  'Above Average': 1,
  'High': 0,
};

function imdDecile(rank) {
  if (!rank || rank < 1) return null;
  const decile = Math.ceil((rank / IMD_MAX_RANK) * 10);
  return Math.max(1, Math.min(10, decile));
}

function imdTier(decile) {
  if (decile == null) return null;
  if (decile <= 2) return 0;
  if (decile <= 4) return 1;
  if (decile <= 6) return 2;
  if (decile <= 8) return 3;
  return 4;
}

export function computeAreaSignal(property) {
  const decile = imdDecile(property?.imd_rank);
  const imdT = imdTier(decile);
  const crimeT = property?.crime_rate_band ? CRIME_TIER[property.crime_rate_band] : null;

  const tiers = [imdT, crimeT].filter(t => t != null);
  if (tiers.length === 0) return null;

  const avg = tiers.reduce((a, b) => a + b, 0) / tiers.length;
  const worst = Math.min(...tiers);

  let level;
  if (worst === 0 || avg < 1.25) level = 'red';
  else if (avg >= 2.75) level = 'green';
  else level = 'amber';

  return { level, decile, crimeBand: property?.crime_rate_band || null };
}

const STYLE = {
  green: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
  amber: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
  red:   'bg-red-500/20 text-red-300 border-red-500/40',
};

const LABEL = {
  green: 'Area · Strong',
  amber: 'Area · Mixed',
  red:   'Area · Tough',
};

const NARRATIVE = {
  green: 'Generally considered a settled, lower-risk area — limited deprivation indicators and lower-than-average recent crime.',
  amber: 'A mixed area — average-to-elevated crime or moderate deprivation. Worth a site visit before committing.',
  red:   'A tougher area on paper — higher deprivation and/or crime than the national average. Some investors target these deliberately for yield; expect cautious lender and tenant-mix considerations.',
};

export function describeAreaSignal(signal, extras = {}) {
  if (!signal) return null;
  const lines = [NARRATIVE[signal.level]];
  if (extras.crimeTrend && extras.crimeTrend !== 'Stable') {
    const trendText = {
      'Improving':   'Recent crime trend is improving.',
      'Worsening':   'Recent crime trend is worsening — worth flagging.',
      'Rising Fast': 'Crime is rising sharply — investigate before committing.',
    }[extras.crimeTrend];
    if (trendText) lines.push(trendText);
  }
  return lines.join(' ');
}

export default function AreaBadge({ property, size = 'sm' }) {
  const signal = computeAreaSignal(property);
  if (!signal) return null;

  const parts = [];
  if (signal.decile != null) {
    parts.push(`IMD decile ${signal.decile}/10 (${signal.decile <= 3 ? 'more deprived' : signal.decile >= 8 ? 'less deprived' : 'mid'})`);
  }
  if (signal.crimeBand) parts.push(`Crime: ${signal.crimeBand}`);
  const tip = `Area indicator combines deprivation index and recent crime rate.\n${parts.join(' · ')}`;

  return (
    <span
      title={tip}
      className={clsx(
        'inline-flex items-center gap-1 font-semibold rounded border',
        size === 'sm' ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1',
        STYLE[signal.level]
      )}
    >
      <span
        className={clsx(
          'inline-block rounded-full',
          size === 'sm' ? 'w-1.5 h-1.5' : 'w-2 h-2',
          signal.level === 'green' && 'bg-emerald-400',
          signal.level === 'amber' && 'bg-amber-400',
          signal.level === 'red' && 'bg-red-400',
        )}
      />
      {LABEL[signal.level]}
    </span>
  );
}
